import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  User,
  Target,
  Sparkles,
  Loader2,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  FolderOpen,
  FileSearch,
  Edit3,
  Trash2,
  Plus,
  X,
  Eye,
  Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXAMINATION_TYPES = [
  { value: 'direct_examination', label: 'Direct Examination', description: 'Questions for your own witness' },
  { value: 'cross_examination', label: 'Cross-Examination', description: 'Questions for opposing witness' },
  { value: 'deposition', label: 'Deposition', description: 'Discovery deposition questions' },
  { value: 'hearing', label: 'Hearing', description: 'Hearing examination outline' },
  { value: 'redirect', label: 'Redirect', description: 'Follow-up to direct examination' },
  { value: 'recross', label: 'Recross', description: 'Follow-up to cross-examination' }
];

const STEPS = [
  { id: 1, title: 'Select Case', description: 'Choose a case and documents', icon: FolderOpen },
  { id: 2, title: 'Analyze Documents', description: 'AI summarizes each document', icon: FileSearch },
  { id: 3, title: 'Witness Details', description: 'Enter witness information', icon: User },
  { id: 4, title: 'Objectives', description: 'Define examination goals', icon: Target },
  { id: 5, title: 'Generate', description: 'Create AI-powered outline', icon: Sparkles }
];

export default function ExaminationWizard({ onComplete, onCancel }) {
  const { token } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Case & Documents
  const [cases, setCases] = useState({ case_management: [], exhibit_list: [] });
  const [selectedCase, setSelectedCase] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  
  // Step 2: Document Analyses
  const [documentAnalyses, setDocumentAnalyses] = useState([]);
  const [analyzingDoc, setAnalyzingDoc] = useState(null);
  const [editingAnalysis, setEditingAnalysis] = useState(null);
  
  // Step 3: Witness Details
  const [witnessDetails, setWitnessDetails] = useState({
    name: '',
    role: '',
    relationship: ''
  });
  
  // Step 4: Objectives
  const [examinationType, setExaminationType] = useState('direct_examination');
  const [title, setTitle] = useState('');
  const [objectives, setObjectives] = useState(['']);
  
  // Step 5: Generation
  const [generating, setGenerating] = useState(false);
  const [generatedOutline, setGeneratedOutline] = useState(null);
  
  // Document Preview
  const [previewDocument, setPreviewDocument] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  
  // Fetch cases on mount
  useEffect(() => {
    fetchCases();
  }, []);
  
  // Fetch documents when case is selected
  useEffect(() => {
    if (selectedCase) {
      fetchDocuments(selectedCase.id);
      fetchExistingAnalyses(selectedCase.id);
    }
  }, [selectedCase]);
  
  const fetchCases = async () => {
    try {
      const res = await fetch(`${API_URL}/api/examination/cases`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCases(data);
      }
    } catch (error) {
      toast.error('Failed to load cases');
    }
  };
  
  const fetchDocuments = async (caseId) => {
    try {
      const res = await fetch(`${API_URL}/api/examination/cases/${caseId}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const allDocs = [
          ...data.case_management_documents.map(d => ({ ...d, source: 'case_management' })),
          ...data.exhibit_documents.map(d => ({ ...d, source: 'exhibit_list' }))
        ];
        setDocuments(allDocs);
      }
    } catch (error) {
      toast.error('Failed to load documents');
    }
  };
  
  const fetchExistingAnalyses = async (caseId) => {
    try {
      const res = await fetch(`${API_URL}/api/examination/document-analyses/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDocumentAnalyses(data);
      }
    } catch (error) {
      console.error('Failed to load existing analyses');
    }
  };
  
  const analyzeDocument = async (doc) => {
    setAnalyzingDoc(doc.id);
    try {
      const res = await fetch(`${API_URL}/api/examination/analyze-document?document_id=${doc.id}&case_id=${selectedCase.id}&source=${doc.source}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const analysis = await res.json();
        setDocumentAnalyses(prev => {
          const existing = prev.findIndex(a => a.document_id === doc.id);
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = analysis;
            return updated;
          }
          return [...prev, analysis];
        });
        toast.success(`Analyzed: ${getDocDisplayName(doc)}`);
      } else {
        toast.error('Analysis failed');
      }
    } catch (error) {
      toast.error('Failed to analyze document');
    } finally {
      setAnalyzingDoc(null);
    }
  };
  
  // Helper to get display name for a document
  const getDocDisplayName = (doc) => {
    return doc.original_filename || doc.filename || doc.name || 'Unnamed Document';
  };
  
  // Preview document content
  const previewDoc = async (doc) => {
    setPreviewDocument(doc);
    setPreviewContent(null);
    setPreviewLoading(true);
    
    try {
      // Check if we have an existing analysis with extracted text
      const existingAnalysis = documentAnalyses.find(a => a.document_id === doc.id);
      if (existingAnalysis?.extracted_text) {
        setPreviewContent({
          type: 'text',
          content: existingAnalysis.extracted_text,
          summary: existingAnalysis.summary,
          keyFacts: existingAnalysis.key_facts
        });
        setPreviewLoading(false);
        return;
      }
      
      // Try to get a text preview from the backend
      const res = await fetch(
        `${API_URL}/api/examination/document-preview/${doc.id}?source=${doc.source}&case_id=${selectedCase.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setPreviewContent({
          type: 'text',
          content: data.text_preview || 'No text content available',
          pageCount: data.page_count,
          fileType: data.file_type
        });
      } else {
        setPreviewContent({
          type: 'error',
          content: 'Unable to preview this document. Try analyzing it first.'
        });
      }
    } catch (error) {
      setPreviewContent({
        type: 'error',
        content: 'Failed to load document preview'
      });
    } finally {
      setPreviewLoading(false);
    }
  };
  
  const analyzeAllSelected = async () => {
    for (const docId of selectedDocuments) {
      const doc = documents.find(d => d.id === docId);
      const existingAnalysis = documentAnalyses.find(a => a.document_id === docId);
      if (doc && !existingAnalysis) {
        await analyzeDocument(doc);
      }
    }
  };
  
  const updateAnalysis = async (analysisId, updates) => {
    try {
      const params = new URLSearchParams();
      if (updates.summary) params.append('summary', updates.summary);
      if (updates.key_facts) updates.key_facts.forEach(f => params.append('key_facts', f));
      
      const res = await fetch(`${API_URL}/api/examination/document-analyses/${analysisId}?${params}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const updated = await res.json();
        setDocumentAnalyses(prev => prev.map(a => a.id === analysisId ? updated : a));
        toast.success('Analysis updated');
        setEditingAnalysis(null);
      }
    } catch (error) {
      toast.error('Failed to update analysis');
    }
  };
  
  const generateOutline = async () => {
    setGenerating(true);
    try {
      const selectedAnalysisIds = documentAnalyses
        .filter(a => selectedDocuments.includes(a.document_id))
        .map(a => a.id);
      
      const params = new URLSearchParams({
        case_id: selectedCase.id,
        title: title || `${examinationType.replace('_', ' ')} of ${witnessDetails.name}`,
        examination_type: examinationType,
        witness_name: witnessDetails.name,
        witness_role: witnessDetails.role,
        witness_relationship: witnessDetails.relationship,
        auto_generate: 'true'
      });
      
      objectives.filter(o => o.trim()).forEach(o => params.append('objectives', o));
      selectedAnalysisIds.forEach(id => params.append('selected_analysis_ids', id));
      
      const res = await fetch(`${API_URL}/api/examination/outlines/create-with-wizard?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const outline = await res.json();
        setGeneratedOutline(outline);
        toast.success('Examination outline generated!');
        if (onComplete) {
          onComplete(outline);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Generation failed');
      }
    } catch (error) {
      toast.error('Failed to generate outline');
    } finally {
      setGenerating(false);
    }
  };
  
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return selectedCase && selectedDocuments.length > 0;
      case 2:
        return selectedDocuments.every(docId => 
          documentAnalyses.some(a => a.document_id === docId && a.analysis_status === 'completed')
        );
      case 3:
        return witnessDetails.name && witnessDetails.role;
      case 4:
        return objectives.some(o => o.trim());
      case 5:
        return true;
      default:
        return false;
    }
  };
  
  const addObjective = () => {
    setObjectives([...objectives, '']);
  };
  
  const updateObjective = (index, value) => {
    const updated = [...objectives];
    updated[index] = value;
    setObjectives(updated);
  };
  
  const removeObjective = (index) => {
    if (objectives.length > 1) {
      setObjectives(objectives.filter((_, i) => i !== index));
    }
  };
  
  const progress = (currentStep / STEPS.length) * 100;
  
  return (
    <div className="flex flex-col h-full bg-background" data-testid="examination-wizard">
      {/* Header with Progress */}
      <div className="border-b bg-card p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold">Create Examination Outline</h2>
            <p className="text-sm text-muted-foreground">Step {currentStep} of {STEPS.length}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Step Indicators */}
        <div className="flex items-center gap-2 mb-2">
          {STEPS.map((step, idx) => (
            <div key={step.id} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  currentStep > step.id
                    ? 'bg-green-500 text-white'
                    : currentStep === step.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {currentStep > step.id ? <CheckCircle className="w-4 h-4" /> : step.id}
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`w-12 h-0.5 mx-1 ${currentStep > step.id ? 'bg-green-500' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>
        <Progress value={progress} className="h-1" />
      </div>
      
      {/* Content Area */}
      <ScrollArea className="flex-1 p-6">
        {/* Step 1: Select Case & Documents */}
        {currentStep === 1 && (
          <div className="space-y-6" data-testid="wizard-step-1">
            <div>
              <h3 className="text-lg font-semibold mb-2">Select Case</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Choose the case for this examination outline
              </p>
              
              <div className="grid gap-3">
                {[...cases.case_management, ...cases.exhibit_list].map(c => (
                  <Card
                    key={c.id}
                    className={`cursor-pointer transition-all ${
                      selectedCase?.id === c.id ? 'ring-2 ring-primary' : 'hover:bg-accent'
                    }`}
                    onClick={() => {
                      setSelectedCase(c);
                      setSelectedDocuments([]);
                    }}
                  >
                    <CardContent className="p-4 flex items-center gap-3">
                      <FolderOpen className="w-5 h-5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="font-medium">{c.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {c.case_number || 'No case number'} • {c.client_name || 'No client'}
                        </p>
                      </div>
                      {selectedCase?.id === c.id && (
                        <CheckCircle className="w-5 h-5 text-primary" />
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            
            {selectedCase && (
              <div>
                <h3 className="text-lg font-semibold mb-2">Select Documents to Analyze</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Select the documents relevant to this examination
                </p>
                
                {documents.length === 0 ? (
                  <Card className="bg-muted/50">
                    <CardContent className="p-6 text-center">
                      <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No documents found for this case</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {documents.map(doc => {
                      const hasAnalysis = documentAnalyses.some(a => a.document_id === doc.id);
                      const isSelected = selectedDocuments.includes(doc.id);
                      const displayName = getDocDisplayName(doc);
                      return (
                        <div
                          key={doc.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                            isSelected ? 'bg-primary/5 border-primary' : 'hover:bg-accent'
                          }`}
                          data-testid={`doc-select-${doc.id}`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedDocuments([...selectedDocuments, doc.id]);
                              } else {
                                setSelectedDocuments(selectedDocuments.filter(id => id !== doc.id));
                              }
                            }}
                          />
                          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <div 
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => {
                              if (isSelected) {
                                setSelectedDocuments(selectedDocuments.filter(id => id !== doc.id));
                              } else {
                                setSelectedDocuments([...selectedDocuments, doc.id]);
                              }
                            }}
                          >
                            <p className="font-medium text-sm truncate" title={displayName}>
                              {displayName}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {doc.source === 'case_management' ? 'Case Document' : 'Exhibit'}
                              {doc.category && ` • ${doc.category}`}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              previewDoc(doc);
                            }}
                            title="Preview document"
                            data-testid={`preview-doc-${doc.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Preview
                          </Button>
                          {hasAnalysis && (
                            <Badge variant="outline" className="text-green-600 flex-shrink-0">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Analyzed
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Step 2: Document Analysis */}
        {currentStep === 2 && (
          <div className="space-y-6" data-testid="wizard-step-2">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Document Analysis</h3>
                <p className="text-sm text-muted-foreground">
                  AI will extract key facts from each document
                </p>
              </div>
              <Button
                onClick={analyzeAllSelected}
                disabled={analyzingDoc !== null}
              >
                {analyzingDoc ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyze All
                  </>
                )}
              </Button>
            </div>
            
            <div className="space-y-4">
              {selectedDocuments.map(docId => {
                const doc = documents.find(d => d.id === docId);
                const analysis = documentAnalyses.find(a => a.document_id === docId);
                const isAnalyzing = analyzingDoc === docId;
                
                if (!doc) return null;
                
                return (
                  <Card key={docId} className={analysis?.analysis_status === 'completed' ? 'border-green-200' : ''}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className="w-4 h-4 flex-shrink-0" />
                          <CardTitle className="text-base truncate" title={getDocDisplayName(doc)}>
                            {getDocDisplayName(doc)}
                          </CardTitle>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => previewDoc(doc)}
                            title="Preview document"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {!analysis ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => analyzeDocument(doc)}
                              disabled={isAnalyzing}
                            >
                              {isAnalyzing ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <FileSearch className="w-4 h-4 mr-1" />
                                  Analyze
                                </>
                              )}
                            </Button>
                          ) : (
                            <Badge variant="outline" className="text-green-600">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Analyzed
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    
                    {analysis && (
                      <CardContent className="pt-0">
                        {editingAnalysis === analysis.id ? (
                          <div className="space-y-3">
                            <div>
                              <Label>Summary</Label>
                              <Textarea
                                defaultValue={analysis.summary}
                                id={`summary-${analysis.id}`}
                                rows={3}
                              />
                            </div>
                            <div>
                              <Label>Key Facts (one per line)</Label>
                              <Textarea
                                defaultValue={analysis.key_facts?.join('\n')}
                                id={`facts-${analysis.id}`}
                                rows={4}
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => {
                                  const summary = document.getElementById(`summary-${analysis.id}`).value;
                                  const facts = document.getElementById(`facts-${analysis.id}`).value.split('\n').filter(f => f.trim());
                                  updateAnalysis(analysis.id, { summary, key_facts: facts });
                                }}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingAnalysis(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Summary</p>
                              <p className="text-sm">{analysis.summary}</p>
                            </div>
                            
                            {analysis.key_facts?.length > 0 && (
                              <div>
                                <p className="text-sm font-medium text-muted-foreground mb-1">Key Facts</p>
                                <ul className="text-sm space-y-1">
                                  {analysis.key_facts.map((fact, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                      <span className="text-primary">•</span>
                                      {fact}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {analysis.key_names?.length > 0 && (
                              <div>
                                <p className="text-sm font-medium text-muted-foreground mb-1">Key People</p>
                                <div className="flex flex-wrap gap-1">
                                  {analysis.key_names.map((name, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      {name}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingAnalysis(analysis.id)}
                            >
                              <Edit3 className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Step 3: Witness Details */}
        {currentStep === 3 && (
          <div className="space-y-6" data-testid="wizard-step-3">
            <div>
              <h3 className="text-lg font-semibold mb-2">Witness Information</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Enter details about the witness being examined
              </p>
            </div>
            
            <div className="grid gap-4">
              <div>
                <Label htmlFor="witness-name">Witness Name *</Label>
                <Input
                  id="witness-name"
                  value={witnessDetails.name}
                  onChange={(e) => setWitnessDetails({ ...witnessDetails, name: e.target.value })}
                  placeholder="e.g., John Smith"
                />
              </div>
              
              <div>
                <Label htmlFor="witness-role">Role in Case *</Label>
                <Select
                  value={witnessDetails.role}
                  onValueChange={(value) => setWitnessDetails({ ...witnessDetails, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Plaintiff">Plaintiff</SelectItem>
                    <SelectItem value="Defendant">Defendant</SelectItem>
                    <SelectItem value="Petitioner">Petitioner</SelectItem>
                    <SelectItem value="Respondent">Respondent</SelectItem>
                    <SelectItem value="Expert Witness">Expert Witness</SelectItem>
                    <SelectItem value="Fact Witness">Fact Witness</SelectItem>
                    <SelectItem value="Character Witness">Character Witness</SelectItem>
                    <SelectItem value="Law Enforcement">Law Enforcement</SelectItem>
                    <SelectItem value="Medical Professional">Medical Professional</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="witness-relationship">Relationship to Case</Label>
                <Textarea
                  id="witness-relationship"
                  value={witnessDetails.relationship}
                  onChange={(e) => setWitnessDetails({ ...witnessDetails, relationship: e.target.value })}
                  placeholder="Describe the witness's connection to the case..."
                  rows={3}
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Step 4: Objectives */}
        {currentStep === 4 && (
          <div className="space-y-6" data-testid="wizard-step-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Examination Setup</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Define the type and objectives of this examination
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Examination Type</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {EXAMINATION_TYPES.map(type => (
                    <Card
                      key={type.value}
                      className={`cursor-pointer transition-all ${
                        examinationType === type.value ? 'ring-2 ring-primary' : 'hover:bg-accent'
                      }`}
                      onClick={() => setExaminationType(type.value)}
                    >
                      <CardContent className="p-3">
                        <p className="font-medium text-sm">{type.label}</p>
                        <p className="text-xs text-muted-foreground">{type.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
              
              <div>
                <Label htmlFor="outline-title">Outline Title</Label>
                <Input
                  id="outline-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={`${examinationType.replace('_', ' ')} of ${witnessDetails.name || 'Witness'}`}
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Examination Objectives</Label>
                  <Button size="sm" variant="ghost" onClick={addObjective}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {objectives.map((obj, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        value={obj}
                        onChange={(e) => updateObjective(idx, e.target.value)}
                        placeholder={`Objective ${idx + 1}: e.g., Establish timeline of events`}
                      />
                      {objectives.length > 1 && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => removeObjective(idx)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Step 5: Generate */}
        {currentStep === 5 && (
          <div className="space-y-6" data-testid="wizard-step-5">
            <div>
              <h3 className="text-lg font-semibold mb-2">Review & Generate</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Review your selections and generate the examination outline
              </p>
            </div>
            
            {/* Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Case</p>
                    <p className="font-medium">{selectedCase?.title}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Documents</p>
                    <p className="font-medium">{selectedDocuments.length} selected</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Witness</p>
                    <p className="font-medium">{witnessDetails.name} ({witnessDetails.role})</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Type</p>
                    <p className="font-medium">{examinationType.replace('_', ' ')}</p>
                  </div>
                </div>
                
                {/* Selected Documents List with Preview */}
                <div>
                  <p className="text-muted-foreground text-sm mb-2">Selected Documents</p>
                  <div className="space-y-1 max-h-32 overflow-auto border rounded p-2 bg-muted/30">
                    {selectedDocuments.map(docId => {
                      const doc = documents.find(d => d.id === docId);
                      if (!doc) return null;
                      return (
                        <div key={docId} className="flex items-center justify-between text-sm py-1">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FileText className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                            <span className="truncate" title={getDocDisplayName(doc)}>
                              {getDocDisplayName(doc)}
                            </span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2"
                            onClick={() => previewDoc(doc)}
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <p className="text-muted-foreground text-sm mb-1">Objectives</p>
                  <ul className="text-sm space-y-1">
                    {objectives.filter(o => o.trim()).map((obj, idx) => (
                      <li key={idx}>• {obj}</li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <p className="text-muted-foreground text-sm mb-1">Key Facts from Documents</p>
                  <ul className="text-sm space-y-1 max-h-40 overflow-auto">
                    {documentAnalyses
                      .filter(a => selectedDocuments.includes(a.document_id))
                      .flatMap(a => a.key_facts || [])
                      .slice(0, 10)
                      .map((fact, idx) => (
                        <li key={idx} className="text-muted-foreground">• {fact}</li>
                      ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
            
            {!generatedOutline ? (
              <Button
                className="w-full"
                size="lg"
                onClick={generateOutline}
                disabled={generating}
                data-testid="generate-outline-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Outline...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Examination Outline
                  </>
                )}
              </Button>
            ) : (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div className="flex-1">
                    <p className="font-medium text-green-900">Outline Generated!</p>
                    <p className="text-sm text-green-700">
                      {generatedOutline.sections?.length || 0} sections with{' '}
                      {generatedOutline.sections?.reduce((sum, s) => sum + (s.questions?.length || 0), 0) || 0} questions
                    </p>
                  </div>
                  <Button onClick={() => onComplete && onComplete(generatedOutline)}>
                    View Outline
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </ScrollArea>
      
      {/* Footer Navigation */}
      <div className="border-t p-4 flex justify-between bg-card">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
          disabled={currentStep === 1}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        
        {currentStep < 5 ? (
          <Button
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={!canProceed()}
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button variant="outline" onClick={onCancel}>
            Close
          </Button>
        )}
      </div>
      
      {/* Document Preview Dialog */}
      <Dialog open={!!previewDocument} onOpenChange={(open) => !open && setPreviewDocument(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Document Preview
            </DialogTitle>
            <DialogDescription>
              {previewDocument && getDocDisplayName(previewDocument)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {previewLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">Loading document preview...</p>
              </div>
            ) : previewContent ? (
              <div className="space-y-4">
                {/* Document Info */}
                {previewDocument && (
                  <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">File Name:</span>
                        <p className="font-medium">{getDocDisplayName(previewDocument)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Source:</span>
                        <p className="font-medium">
                          {previewDocument.source === 'case_management' ? 'Case Management' : 'Exhibit List'}
                        </p>
                      </div>
                      {previewDocument.category && (
                        <div>
                          <span className="text-muted-foreground">Category:</span>
                          <p className="font-medium capitalize">{previewDocument.category}</p>
                        </div>
                      )}
                      {previewContent.pageCount && (
                        <div>
                          <span className="text-muted-foreground">Pages:</span>
                          <p className="font-medium">{previewContent.pageCount}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* AI Summary (if available) */}
                {previewContent.summary && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      AI Summary
                    </h4>
                    <p className="text-sm text-muted-foreground">{previewContent.summary}</p>
                  </div>
                )}
                
                {/* Key Facts (if available) */}
                {previewContent.keyFacts && previewContent.keyFacts.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Key Facts
                    </h4>
                    <ul className="text-sm space-y-1">
                      {previewContent.keyFacts.map((fact, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-primary">•</span>
                          <span className="text-muted-foreground">{fact}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Text Content Preview */}
                {previewContent.type === 'text' && previewContent.content && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Document Content Preview
                    </h4>
                    <div className="bg-muted/30 rounded p-3 max-h-[300px] overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap font-mono text-muted-foreground">
                        {previewContent.content.length > 5000 
                          ? previewContent.content.substring(0, 5000) + '\n\n... [Preview truncated - analyze document for full content]'
                          : previewContent.content
                        }
                      </pre>
                    </div>
                  </div>
                )}
                
                {/* Error State */}
                {previewContent.type === 'error' && (
                  <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertCircle className="w-5 h-5" />
                      <p className="text-sm">{previewContent.content}</p>
                    </div>
                    <p className="text-xs text-amber-600 mt-2">
                      Tip: Select this document and click "Analyze Selected" in Step 2 to extract and preview content.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No preview available</p>
              </div>
            )}
          </div>
          
          <div className="border-t pt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPreviewDocument(null)}>
              Close
            </Button>
            {previewDocument && !documentAnalyses.some(a => a.document_id === previewDocument.id) && (
              <Button 
                onClick={() => {
                  // Add to selection and close preview
                  if (!selectedDocuments.includes(previewDocument.id)) {
                    setSelectedDocuments([...selectedDocuments, previewDocument.id]);
                  }
                  setPreviewDocument(null);
                  toast.success('Document selected for analysis');
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                Select for Analysis
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
